import cx from 'classnames'
import { useKeenSlider } from 'keen-slider/react'
import { useCallback, useMemo, useState } from 'react'

import { type GalleryCarouselItem, WheelControls } from '@lib/gallery'
import { screens } from '@lib/theme'

import ModalGallery from '@components/modal-gallery'
import GalleryCarouselControls from './controls'
import GalleryCarouselSlide from './slide'
import GalleryCarouselThumbnail from './thumbnail'

export interface GalleryCarouselProps {
  id?: string
  items: GalleryCarouselItem[]
  hasArrows?: boolean
  hasDots?: boolean
  hasCounter?: boolean
  hasDrag?: boolean
  hasClickToZoom?: boolean
  hasThumbnails?: boolean
  cornerControls?: boolean
  preloadFirstImage?: boolean
  className?: string
}

const GalleryCarousel = ({
  id,
  items,
  hasArrows,
  hasDots,
  hasCounter,
  hasDrag,
  hasClickToZoom,
  hasThumbnails,
  cornerControls,
  preloadFirstImage,
  className,
}: GalleryCarouselProps) => {
  const [selectedItemIndex, setSelectedItemIndex] = useState(0)
  const [selectedModalImageIndex, setSelectedModalImageIndex] = useState<
    number | null
  >(null)
  const [isModalOpen, setIsModalOpen] = useState(false)

  const [sliderRef, slider] = useKeenSlider<HTMLDivElement>({
    initial: 0,
    selector: '.carousel-slide',
    defaultAnimation: {
      duration: 800,
    },
    dragSpeed: 0.8,
    drag: hasDrag,
    slideChanged(slider) {
      const newIndex = slider.track.details.rel
      setSelectedItemIndex(newIndex)
      thumbnailSlider.current?.moveToIdx(newIndex)
    },
  })

  const [thumbnailRef, thumbnailSlider] = useKeenSlider<HTMLDivElement>(
    {
      initial: 0,
      selector: '.carousel-thumbnail',
      mode: 'free',
      defaultAnimation: {
        duration: 800,
      },
      drag: true,
      slides: {
        spacing: 8,
        perView: 'auto',
      },
      breakpoints: {
        [`(min-width: ${screens.md})`]: {
          vertical: true,
          rubberband: false,
        },
      },
    },
    [WheelControls],
  )

  const modalImages = useMemo(() => {
    return items
      .filter((item) => item.type === 'image')
      .map((item) => item.image)
  }, [items])

  const handleClick = useCallback(
    (index: number) => {
      const item = items[index]

      if (!item) {
        return
      }

      if (hasClickToZoom && item.type === 'image') {
        // Determine image index based on item index
        const imageItems = items.filter((item) => item.type === 'image')
        const newSelectedModalImageIndex = imageItems.findIndex(
          (imageItem) => imageItem.image.asset._id === item.image.asset._id,
        )

        if (newSelectedModalImageIndex > -1) {
          setSelectedModalImageIndex(newSelectedModalImageIndex)
        }

        setIsModalOpen(true)
      }
    },
    [hasClickToZoom, items],
  )

  const closeModal = useCallback(() => {
    setIsModalOpen(false)
    setSelectedModalImageIndex(null)
  }, [])

  return (
    <div
      id={id}
      className={cx(
        {
          'flex flex-col gap-2 relative md:pr-[calc(6rem+8px)] overflow-hidden':
            hasThumbnails,
        },
        className,
      )}
    >
      <div className="relative overflow-hidden grow-0">
        <div
          ref={sliderRef}
          className={cx(
            'flex relative overflow-hidden will-change-transform touch-action touch-action-pan-y',
            {
              'cursor-grab': hasDrag && !hasClickToZoom,
              'cursor-pointer': hasClickToZoom,
              'active:cursor-grabbing': hasDrag,
            },
          )}
        >
          {items.map((item, index) => (
            <GalleryCarouselSlide
              key={`slide-${item.id}`}
              index={index}
              item={item}
              preload={preloadFirstImage && index === 0}
              onClick={handleClick}
            />
          ))}
        </div>

        {(hasArrows || hasDots) && items.length > 1 && (
          <GalleryCarouselControls
            id={id}
            slider={slider.current}
            currentSlide={selectedItemIndex}
            slideCount={items.length}
            hasArrows={hasArrows}
            hasDots={hasDots}
            hasCounter={hasCounter}
            cornerControls={cornerControls}
          />
        )}
      </div>

      {hasThumbnails && (
        <div
          ref={thumbnailRef}
          className="flex md:absolute md:right-0 md:inset-y-0 md:flex-col"
        >
          {items.map((item, index) => (
            <GalleryCarouselThumbnail
              key={`thumbnail-${item.id}`}
              item={item}
              isSelected={index === selectedItemIndex}
              onClick={() => {
                setSelectedItemIndex(index)
                slider.current?.moveToIdx(index)
              }}
            />
          ))}
        </div>
      )}

      <ModalGallery
        isModalOpen={isModalOpen}
        images={modalImages}
        initialImageIndex={selectedModalImageIndex}
        onClose={closeModal}
      />
    </div>
  )
}

export default GalleryCarousel
